import React, { useState } from 'react';
import { Plus, Trash2, Info } from 'lucide-react';
export type PaymentOption = {
  id: string;
  settlementType: string;
  settlementPercent: string;
  downPaymentType: string;
  downPaymentAmount: string;
  frequency: string;
  numPayments: string;
  dueWithinDays: string;
};
export type Arrangement = {
  id: string;
  name: string;
  balanceMin: string;
  balanceMax: string;
  paymentOptions: PaymentOption[];
};
export function OfferForm({
  onClose
}: {
  onClose: () => void;
}) {
  const [arrangements, setArrangements] = useState<Arrangement[]>([{
    id: '1',
    name: '500_1000',
    balanceMin: '500',
    balanceMax: '1000',
    paymentOptions: [{
      id: '1',
      settlementType: 'Settlement Payment Arrangement',
      settlementPercent: '85',
      downPaymentType: 'Amount',
      downPaymentAmount: '100.00',
      frequency: 'SET - Monthly',
      numPayments: '2',
      dueWithinDays: '30'
    }]
  }]);
  const addArrangement = () => {
    setArrangements([...arrangements, {
      id: String(Date.now()),
      name: '',
      balanceMin: '',
      balanceMax: '',
      paymentOptions: [{
        id: '1',
        settlementType: 'Settlement Payment Arrangement',
        settlementPercent: '',
        downPaymentType: 'Amount',
        downPaymentAmount: '',
        frequency: 'SET - Monthly',
        numPayments: '',
        dueWithinDays: ''
      }]
    }]);
  };
  const removeArrangement = (id: string) => {
    setArrangements(arrangements.filter(a => a.id !== id));
  };
  const updateArrangement = (id: string, field: string, value: string) => {
    setArrangements(arrangements.map(a => a.id === id ? {
      ...a,
      [field]: value
    } : a));
  };
  const addPaymentOption = (arrangementId: string) => {
    setArrangements(arrangements.map(a => a.id === arrangementId ? {
      ...a,
      paymentOptions: [...a.paymentOptions, {
        id: String(Date.now()),
        settlementType: 'Settlement Payment Arrangement',
        settlementPercent: '',
        downPaymentType: 'Amount',
        downPaymentAmount: '',
        frequency: 'SET - Monthly',
        numPayments: '',
        dueWithinDays: ''
      }]
    } : a));
  };
  const removePaymentOption = (arrangementId: string, optionId: string) => {
    setArrangements(arrangements.map(a => a.id === arrangementId ? {
      ...a,
      paymentOptions: a.paymentOptions.filter(o => o.id !== optionId)
    } : a));
  };
  const updatePaymentOption = (arrangementId: string, optionId: string, field: string, value: string) => {
    setArrangements(arrangements.map(a => a.id === arrangementId ? {
      ...a,
      paymentOptions: a.paymentOptions.map(o => o.id === optionId ? {
        ...o,
        [field]: value
      } : o)
    } : a));
  };
  return <div className="space-y-6" data-id="element-39">
      {/* Offer Details */}
      <div className="space-y-4" data-id="element-40">
        <h3 className="text-sm font-bold text-brand-navy uppercase tracking-wider" data-id="element-41">
          Offer Details
        </h3>
        <div className="grid grid-cols-2 gap-4" data-id="element-42">
          <div data-id="element-43">
            <label className="block text-xs font-medium text-gray-600 mb-1.5" data-id="element-44">
              Code*
            </label>
            <input type="text" defaultValue="Taxes2026" className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-brand-navy focus:ring-1 focus:ring-brand-navy" data-id="element-45" />
          </div>
          <div data-id="element-46"></div>
        </div>
        <div data-id="element-47">
          <label className="block text-xs font-medium text-gray-600 mb-1.5" data-id="element-48">
            Description*
          </label>
          <input type="text" defaultValue="Settlement Offers for 2026 Tax Season" className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-brand-navy focus:ring-1 focus:ring-brand-navy" data-id="element-49" />
        </div>
        <div className="grid grid-cols-2 gap-4" data-id="element-50">
          <div data-id="element-51">
            <label className="block text-xs font-medium text-gray-600 mb-1.5" data-id="element-52">
              Offer Start Date
            </label>
            <input type="date" defaultValue="2026-01-01" className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-brand-navy focus:ring-1 focus:ring-brand-navy" data-id="element-53" />
          </div>
          <div data-id="element-54">
            <label className="block text-xs font-medium text-gray-600 mb-1.5" data-id="element-55">
              Offer Expiration Date
            </label>
            <input type="date" defaultValue="2026-04-15" className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-brand-navy focus:ring-1 focus:ring-brand-navy" data-id="element-56" />
          </div>
        </div>
      </div>

      {/* Balance Arrangements */}
      <div className="space-y-4" data-id="element-57">
        <div className="flex items-start justify-between" data-id="element-58">
          <div data-id="element-59">
            <h3 className="text-sm font-bold text-brand-navy uppercase tracking-wider" data-id="element-60">
              Balance Arrangements
            </h3>
            <p className="text-xs text-gray-500 mt-1" data-id="element-61">
              Define rules that match consumer balance ranges, each with one or
              more payment options.
            </p>
          </div>
          <button onClick={addArrangement} className="text-xs font-bold text-brand-red hover:text-red-800 flex items-center gap-1 transition-colors" data-id="element-62">
            <Plus size={14} data-id="element-63" />
            Add Arrangement
          </button>
        </div>

        {arrangements.map(arrangement => <div key={arrangement.id} className="border border-gray-300 rounded-lg p-4 bg-gray-50 space-y-4" data-id="element-64">
            <div className="flex items-start justify-between gap-4" data-id="element-65">
              <div className="flex-1" data-id="element-66">
                <input type="text" value={arrangement.name} onChange={e => updateArrangement(arrangement.id, 'name', e.target.value)} placeholder="e.g. 500_1000" className="w-full px-3 py-2 border border-gray-300 rounded text-sm font-mono focus:outline-none focus:border-brand-navy focus:ring-1 focus:ring-brand-navy bg-white" data-id="element-67" />
              </div>
              <button onClick={() => removeArrangement(arrangement.id)} className="p-2 text-gray-400 hover:text-brand-red transition-colors" data-id="element-68">
                <Trash2 size={16} data-id="element-69" />
              </button>
            </div>

            {arrangement.balanceMin && arrangement.balanceMax && <div className="bg-white border border-gray-200 rounded px-3 py-2" data-id="element-70">
                <code className="text-xs font-mono text-blue-700" data-id="element-71">
                  ((OverallBalance &gt;= {arrangement.balanceMin}) AND
                  (OverallBalance &lt;= {arrangement.balanceMax}))
                </code>
              </div>}

            <div className="grid grid-cols-2 gap-4" data-id="element-72">
              <div data-id="element-73">
                <label className="block text-xs font-medium text-gray-600 mb-1.5" data-id="element-74">
                  Balance Min
                </label>
                <input type="number" value={arrangement.balanceMin} onChange={e => updateArrangement(arrangement.id, 'balanceMin', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-brand-navy focus:ring-1 focus:ring-brand-navy bg-white" data-id="element-75" />
              </div>
              <div data-id="element-76">
                <label className="block text-xs font-medium text-gray-600 mb-1.5" data-id="element-77">
                  Balance Max
                </label>
                <input type="number" value={arrangement.balanceMax} onChange={e => updateArrangement(arrangement.id, 'balanceMax', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-brand-navy focus:ring-1 focus:ring-brand-navy bg-white" data-id="element-78" />
              </div>
            </div>

            <div className="space-y-3" data-id="element-79">
              {arrangement.paymentOptions.map((option, idx) => <div key={option.id} className="bg-white border border-gray-200 rounded-lg p-4 space-y-3" data-id="element-80">
                  <div className="flex items-center justify-between" data-id="element-81">
                    <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider" data-id="element-82">
                      Payment Option {idx + 1}
                    </h4>
                    {arrangement.paymentOptions.length > 1 && <button onClick={() => removePaymentOption(arrangement.id, option.id)} className="text-xs text-gray-400 hover:text-brand-red transition-colors" data-id="element-83">
                        <Trash2 size={14} data-id="element-84" />
                      </button>}
                  </div>

                  <div data-id="element-85">
                    <label className="block text-xs font-medium text-gray-600 mb-1.5" data-id="element-86">
                      Settlement Type*
                    </label>
                    <select value={option.settlementType} onChange={e => updatePaymentOption(arrangement.id, option.id, 'settlementType', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-brand-navy focus:ring-1 focus:ring-brand-navy" data-id="element-87">
                      <option data-id="element-88">Settlement Payment Arrangement</option>
                      <option data-id="element-89">Lump Sum Settlement</option>
                      <option data-id="element-90">Payment Plan</option>
                    </select>
                  </div>

                  <div data-id="element-91">
                    <label className="block text-xs font-medium text-gray-600 mb-1.5" data-id="element-92">
                      Settlement %*
                    </label>
                    <input type="text" value={option.settlementPercent} onChange={e => updatePaymentOption(arrangement.id, option.id, 'settlementPercent', e.target.value)} placeholder="e.g. 85" className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-brand-navy focus:ring-1 focus:ring-brand-navy" data-id="element-93" />
                  </div>

                  <div className="grid grid-cols-2 gap-4" data-id="element-94">
                    <div data-id="element-95">
                      <label className="block text-xs font-medium text-gray-600 mb-1.5" data-id="element-96">
                        Down Payment Type*
                      </label>
                      <select value={option.downPaymentType} onChange={e => updatePaymentOption(arrangement.id, option.id, 'downPaymentType', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-brand-navy focus:ring-1 focus:ring-brand-navy" data-id="element-97">
                        <option data-id="element-98">Amount</option>
                        <option data-id="element-99">Percentage</option>
                      </select>
                    </div>
                    <div data-id="element-100">
                      <label className="block text-xs font-medium text-gray-600 mb-1.5" data-id="element-101">
                        Down Payment*
                      </label>
                      <input type="text" value={option.downPaymentAmount} onChange={e => updatePaymentOption(arrangement.id, option.id, 'downPaymentAmount', e.target.value)} placeholder="$100.00" className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-brand-navy focus:ring-1 focus:ring-brand-navy" data-id="element-102" />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4" data-id="element-103">
                    <div data-id="element-104">
                      <label className="block text-xs font-medium text-gray-600 mb-1.5" data-id="element-105">
                        Frequency*
                      </label>
                      <select value={option.frequency} onChange={e => updatePaymentOption(arrangement.id, option.id, 'frequency', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-brand-navy focus:ring-1 focus:ring-brand-navy" data-id="element-106">
                        <option data-id="element-107">SET - Monthly</option>
                        <option data-id="element-108">SET - Bi-Weekly</option>
                        <option data-id="element-109">SET - Weekly</option>
                      </select>
                    </div>
                    <div data-id="element-110">
                      <label className="block text-xs font-medium text-gray-600 mb-1.5" data-id="element-111">
                        # of Payments*
                      </label>
                      <input type="text" value={option.numPayments} onChange={e => updatePaymentOption(arrangement.id, option.id, 'numPayments', e.target.value)} placeholder="2" className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-brand-navy focus:ring-1 focus:ring-brand-navy" data-id="element-112" />
                    </div>
                    <div data-id="element-113">
                      <label className="flex items-center gap-1 text-xs font-medium text-gray-600 mb-1.5" data-id="element-114">
                        Due w/n # of Days
                        <Info size={12} className="text-gray-400" data-id="element-115" />
                      </label>
                      <input type="text" value={option.dueWithinDays} onChange={e => updatePaymentOption(arrangement.id, option.id, 'dueWithinDays', e.target.value)} placeholder="30" className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-brand-navy focus:ring-1 focus:ring-brand-navy" data-id="element-116" />
                    </div>
                  </div>
                </div>)}

              <button onClick={() => addPaymentOption(arrangement.id)} className="text-xs font-bold text-brand-red hover:text-red-800 flex items-center gap-1 transition-colors" data-id="element-117">
                <Plus size={14} data-id="element-118" />
                Add Payment Option
              </button>
            </div>
          </div>)}
      </div>

      {/* Footer */}
      <div className="pt-6 border-t border-gray-200 flex gap-3 sticky bottom-0 bg-white" data-id="element-119">
        <button onClick={onClose} className="flex-1 px-4 py-2.5 text-sm font-bold text-brand-navy border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-120">
          Cancel
        </button>
        <button onClick={onClose} className="flex-1 px-4 py-2.5 text-sm font-bold bg-brand-red text-white rounded-lg hover:bg-red-800 transition-colors shadow-sm" data-id="element-121">
          Save
        </button>
      </div>
    </div>;
}